import React, { useEffect, useState } from "react";
import styles from "./book.module.scss";
import BusLayout from "../../components/BusLayout";
import {
  IBusLayout,
  SEAT_LAYOUT,
  SEAT_STYLE,
  SeatAvailability,
} from "../../common/types";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_CONSTANTS } from "../../common/constants/api";
import { SESSION_KEYS } from "../../common/constants/sessionKeys";

const BusBook = () => {
  const [searchParams] = useSearchParams();
  const [seats, setSeats] = useState<number>(0);
  const [seatsPrice, setSeatsPrice] = useState<number>(0);
  const [sleepers, setSleepers] = useState<number>(0);
  const [sleepersPrice, setSleepersPrice] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [fromLocation, setFromLocation] = useState<string | null>();
  const [toLocation, setToLocation] = useState<string | null>();
  const [busName, setBusName] = useState<string | null>();
  const [journeyDate, setJourneyDate] = useState<string | undefined | null>();
  const [busRouteMappingId, setBusRouteMappingId] = useState<
    string | undefined | null
  >();
  const [isAc, setIsAc] = useState<boolean>(false);
  const [seatLayout, setSeatLayout] = useState<SEAT_LAYOUT>(
    SEAT_LAYOUT.SINGLE_LAYER_SEATING_3X2
  );
  const [seatStyle, setSeatStyle] = useState<SEAT_STYLE>(SEAT_STYLE.SEATING);
  const [seatingSeats, setSeatingSeats] = useState<SeatAvailability[]>([]);
  const [sleepingSeats, setSleepingSeats] = useState<SeatAvailability[]>([]);
  const [isSingleFirstSeat, setIsSingleFirstSeat] = useState<boolean>(false);
  const [busId, setBusId] = useState<string | undefined | null>();
  const [layoutLoading, setLayoutLoading] = useState(false);

  const toggleSeatHandler = (seats: SeatAvailability[]) => {
    const _tmp: SeatAvailability[] = seats.filter((seat: SeatAvailability) => {
      return seat.isSelected && seat.isAvailable;
    });
    setSeats(_tmp.length);
    const seatsPrice = _tmp.reduce(
      (prevValue: number, currentSeat: SeatAvailability) =>
        prevValue + currentSeat.price,
      0
    );
    setSeatsPrice(seatsPrice);
    setTotalPrice(seatsPrice + sleepersPrice);
    sessionStorage.setItem(SESSION_KEYS.SEATING_SEATS, JSON.stringify(_tmp));
    sessionStorage.setItem(
      SESSION_KEYS.TOTAL_PRICE,
      JSON.stringify(seatsPrice + sleepersPrice)
    );
  };

  const toggleSleepingHandler = (seats: SeatAvailability[]) => {
    const _tmp: SeatAvailability[] = seats.filter((seat: SeatAvailability) => {
      return seat.isSelected && seat.isAvailable;
    });
    setSleepers(_tmp.length);
    const sleeperPrice = _tmp.reduce(
      (prevValue: number, currentSeat: SeatAvailability) =>
        prevValue + currentSeat.price,
      0
    );
    setSleepersPrice(sleeperPrice);
    setTotalPrice(seatsPrice + sleeperPrice);
    sessionStorage.setItem(SESSION_KEYS.SLEEPING_SEATS, JSON.stringify(_tmp));
    sessionStorage.setItem(
      SESSION_KEYS.TOTAL_PRICE,
      JSON.stringify(seatsPrice + sleeperPrice)
    );
  };

  useEffect(() => {
    setIsAc(false);
    setSeatLayout(SEAT_LAYOUT.SINGLE_LAYER_SEATING_3X2);
    setSeatStyle(SEAT_STYLE.SEATING);
    setSeatingSeats([]);
    setSleepingSeats([]);
    setIsSingleFirstSeat(false);
  }, []);

  useEffect(() => {
    setBusId(searchParams.get("busId"));
    setJourneyDate(searchParams.get("date"));
    setBusRouteMappingId(searchParams.get("busRouteMappingId"));
    setToLocation(searchParams.get("to"));
    setFromLocation(searchParams.get("from"));
    setBusName(searchParams.get("busName"));
  }, [searchParams]);

  useEffect(() => {
    if (busId && busRouteMappingId && journeyDate) {
      sessionStorage.removeItem(SESSION_KEYS.SLEEPING_SEATS);
      sessionStorage.removeItem(SESSION_KEYS.SEATING_SEATS);
      setLayoutLoading(true);
      axios
        .post(`${API_CONSTANTS.BUS_LAYOUT}`, {
          busRouteMappingId: busRouteMappingId,
          busId: busId,
          onDate: journeyDate,
          fromStop: sessionStorage.getItem(SESSION_KEYS.FROM_LOCATION_CODE),
          toStop: sessionStorage.getItem(SESSION_KEYS.TO_LOCATION_CODE),
        })
        .then((res) => {
          const _busLayout: IBusLayout = res.data;
          setIsAc(_busLayout.isAc);
          setSeatingSeats(_busLayout.seatingSeats);
          setSleepingSeats(_busLayout.sleepingSeats);
          setIsSingleFirstSeat(_busLayout.isSingleFirstSeats);
          setSeatLayout(_busLayout.seatLayout);
          setSeatStyle(_busLayout.seatStyle);
        })
        .finally(() => {
          setLayoutLoading(false);
        });
    }
  }, [busId, busRouteMappingId, journeyDate]);

  function onCheckoutClickHandler(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    event.preventDefault();
    if (busId && journeyDate && busRouteMappingId && totalPrice > 0) {
      window.location.href = `/checkout?busId=${busId}&date=${journeyDate}&busRouteMappingId=${busRouteMappingId}`;
    }
  }

  const journeyLabel = formatJourneyDate(journeyDate);
  const coachType = layoutLabel(seatStyle);
  const selectedCount = seats + sleepers;

  return (
    <main className="container pageContainer">
      <section className={styles.tripCard}>
        <div className={styles.route}>
          <div className={styles.stop}>
            <span className={styles.stopLabel}>From</span>
            <strong>{fromLocation || "—"}</strong>
          </div>
          <span className={styles.routeLine} aria-hidden="true" />
          <div className={`${styles.stop} ${styles.stopEnd}`}>
            <span className={styles.stopLabel}>To</span>
            <strong>{toLocation || "—"}</strong>
          </div>
        </div>
        <div className={styles.tripMeta}>
          <span>{journeyLabel || journeyDate}</span>
          {busName && <span>{busName}</span>}
          {coachType && <span>{coachType}</span>}
          <span className={isAc ? styles.acPill : styles.nonAcPill}>
            {isAc ? "AC" : "Non AC"}
          </span>
        </div>
      </section>

      <div className={styles.legendBar}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendSwatch} ${styles.swatchAvailable}`} />
          Available
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendSwatch} ${styles.swatchBooked}`} />
          Booked
        </div>
        <div className={styles.legendItem}>
          <div
            className={`${styles.legendSwatch} ${styles.swatchLadiesBooked}`}
            aria-hidden="true"
          />
          Ladies Booked
        </div>
        <div className={styles.legendItem}>
          <div
            className={`${styles.legendSwatch} ${styles.swatchLadiesOnly}`}
            aria-hidden="true"
          />
          Ladies Only Available
        </div>
        <div className={styles.legendItem}>
          <div
            className={`${styles.legendSwatch} ${styles.swatchUnavailable}`}
          />
          Not Available
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendSwatch} ${styles.swatchSelected}`} />
          Selected
        </div>
      </div>

      <div className={styles.layoutWrapper}>
        <div className={`${styles.seatMapSection} ${styles.coach}`}>
          {layoutLoading && (
            <div className={styles.processing} role="status">
              <div className="spinner-border text-primary" aria-hidden="true" />
              <span>Loading seats</span>
            </div>
          )}
          <div className={styles.seatMapHead}>
            <div className={styles.seatMapTitle}>Seat Layout</div>
            <span className={styles.frontTag}>
              <span className={styles.wheel} aria-hidden="true" />
              Front of the bus
            </span>
          </div>
          <BusLayout
            isAc={isAc}
            seatLayout={seatLayout}
            seatStyle={seatStyle}
            seatingSeats={seatingSeats}
            sleepingSeats={sleepingSeats}
            isSingleFirstSeats={isSingleFirstSeat}
            onToggleSeats={toggleSeatHandler}
            onToggleSleepers={toggleSleepingHandler}
          />
        </div>

        <aside className={styles.summaryPanel}>
          <p className={styles.summaryKicker}>Your ticket</p>
          <div className={styles.summaryTitle}>Fare Summary</div>
          <div className={styles.summaryRoute}>
            <strong>{fromLocation || "—"}</strong>
            <span>to</span>
            <strong>{toLocation || "—"}</strong>
          </div>
          <div className={styles.summaryRow}>
            <span>Seats ({seats})</span>
            <span className={styles.summaryRowValue}>₹{seatsPrice}</span>
          </div>
          {sleepingSeats.length > 0 && (
            <div className={styles.summaryRow}>
              <span>Sleepers ({sleepers})</span>
              <span className={styles.summaryRowValue}>₹{sleepersPrice}</span>
            </div>
          )}
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total</span>
            <span className={styles.totalPrice}>₹{totalPrice}</span>
          </div>
          <p className={styles.summaryHint}>
            {selectedCount === 0
              ? "Select at least one seat to continue."
              : `${selectedCount} seat${selectedCount === 1 ? "" : "s"} selected.`}
          </p>
          <button
            className={styles.checkoutBtn}
            onClick={onCheckoutClickHandler}
            disabled={totalPrice === 0}
          >
            Proceed to Checkout
          </button>
        </aside>
      </div>
    </main>
  );
};

function formatJourneyDate(value: string | null | undefined) {
  if (!value) {
    return "";
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function layoutLabel(style?: SEAT_STYLE) {
  if (style === SEAT_STYLE.SEATING) {
    return "Seater";
  }
  if (style === SEAT_STYLE.SLEEPER) {
    return "Sleeper";
  }
  if (style === SEAT_STYLE.SEATING_AND_SLEEPER) {
    return "Seater + Sleeper";
  }
  return "";
}

export default BusBook;
